import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PRESS_MENTIONS } from "../data/pressMentions";

/**
 * PressBadge — bandeau "Vu dans la presse"
 * ========================================
 * Encart place sur les pages a fort trafic (accueil, associations)
 * pour rassurer visiteurs et associations prospects.
 *
 * 26/10/2026 (Lucas) : le bandeau defile tout seul, comme les partenaires
 * associatifs de l'accueil (meme mecanique que PartnersWidget) : liste
 * dupliquee, boucle continue via requestAnimationFrame, pause au survol /
 * pendant un swipe, aucun mouvement si prefers-reduced-motion.
 *
 * Source unique : ../data/pressMentions (partagee avec PressPage).
 */
const SPEED = 0.35; // px / frame ≈ 21 px/s : assez lent pour lire la citation

export default function PressBadge({ className = "" }) {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0);
  const mentions = PRESS_MENTIONS;
  const defile = mentions.length > 1;

  useEffect(() => {
    const el = trackRef.current;
    if (!el || !defile) return undefined;
    const reduce = window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    let raf;
    const step = () => {
      if (pausedRef.current) {
        posRef.current = el.scrollLeft; // resynchronise apres un swipe manuel
      } else {
        const half = el.scrollWidth / 2;
        posRef.current += SPEED;
        if (half > 0 && posRef.current >= half) posRef.current -= half;
        el.scrollLeft = posRef.current;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [defile]);

  if (mentions.length === 0) return null;

  const pause = () => { pausedRef.current = true; };
  const resumeSoon = () => { setTimeout(() => { pausedRef.current = false; }, 1500); };
  // Liste dupliquee pour la boucle continue ; la copie est masquee aux
  // lecteurs d'ecran et sortie de l'ordre de tabulation.
  const loop = defile ? [...mentions, ...mentions] : mentions;

  return (
    <div className={className} data-testid="press-badge">
      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide px-4"
        onMouseEnter={pause}
        onMouseLeave={() => { pausedRef.current = false; }}
        onFocus={pause}
        onBlur={() => { pausedRef.current = false; }}
        onTouchStart={pause}
        onTouchEnd={resumeSoon}
        onPointerDown={pause}
        onPointerUp={resumeSoon}
      >
        {loop.map((m, i) => {
          const copie = i >= mentions.length;
          return (
            <a
              key={`${m.url}-${i}`}
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-hidden={copie ? "true" : undefined}
              tabIndex={copie ? -1 : undefined}
              className={`shrink-0 ${defile ? "w-[300px] sm:w-[380px]" : "w-full max-w-3xl mx-auto"} flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 hover:border-[#1DA1F2] hover:shadow-sm transition-all group`}
            >
              <img
                src={m.logo}
                alt={m.outlet}
                loading="lazy"
                className={`${m.logoClass || "h-7"} w-auto max-w-[96px] flex-shrink-0 object-contain`}
              />
              <div className="flex-1 min-w-0 border-l border-slate-200 pl-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#1DA1F2] leading-none mb-1">
                  Vu dans la presse
                </p>
                <p className="text-sm text-slate-700 italic line-clamp-2 group-hover:text-slate-900">
                  «&nbsp;{m.quote}&nbsp;»
                </p>
              </div>
            </a>
          );
        })}
      </div>

      <div className="text-center mt-2">
        <Link to="/presse" className="text-[11px] text-slate-400 hover:text-[#1DA1F2]">
          Voir l'espace presse
        </Link>
      </div>
    </div>
  );
}
